import { CardData } from '../types/types';

/**
 * Updates which cards on the board are visible (not blocked by other cards)
 * @param cards - The current array of cards
 * @returns {boolean} - Whether any card's visibility was changed
 */
export const updateCardVisibility = (cards: CardData[]): boolean => {
  const updatedCards = [...cards];
  let visibilityChanged = false;
  
  // First get all cards DOM elements with their positions for overlap calculation
  const cardElements: Record<string, DOMRect | null> = {};
  
  // Only get elements for cards that are not already selected
  updatedCards.filter(card => !card.selected).forEach(card => {
    const element = document.getElementById(card.id);
    cardElements[card.id] = element ? element.getBoundingClientRect() : null;
  });
  
  updatedCards.forEach(card => {
    // Skip already selected cards
    if (card.selected) return;
    
    // First check: A card is visible only if all cards that block it have been selected
    let isBlocked = card.blockedBy.some(blockerId => {
      const blocker = updatedCards.find(c => c.id === blockerId);
      return blocker && !blocker.selected;
    });
    
    // If card is not blocked by its blockers, check coverage
    if (!isBlocked) {
      const cardRect = cardElements[card.id];
      
      if (cardRect) {
        // Calculate how much this card is covered by others
        let totalCoveredArea = 0;
        const cardArea = cardRect.width * cardRect.height;
        
        // Check all cards with higher z-index (layer)
        updatedCards
          .filter(otherCard => 
            otherCard.id !== card.id && 
            !otherCard.selected && 
            otherCard.layer > card.layer)
          .forEach(otherCard => {
            const otherRect = cardElements[otherCard.id];
            
            if (otherRect) {
              // Calculate overlap area
              const xOverlap = Math.max(0, 
                Math.min(cardRect.right, otherRect.right) - 
                Math.max(cardRect.left, otherRect.left)
              );
              
              const yOverlap = Math.max(0,
                Math.min(cardRect.bottom, otherRect.bottom) - 
                Math.max(cardRect.top, otherRect.top)
              );
              
              const overlapArea = xOverlap * yOverlap;
              totalCoveredArea += overlapArea;
            }
          });
        
        // Calculate coverage ratio
        const coverageRatio = totalCoveredArea / cardArea;
        
        // If more than 25% covered, consider it not visible
        if (coverageRatio > 0.25) {
          isBlocked = true;
        }
      }
    }
    
    const newVisibility = !isBlocked;
    if (card.visible !== newVisibility) {
      visibilityChanged = true;
      card.visible = newVisibility;
    }
  });
  
  return visibilityChanged;
}; 